/**
 * Каталог ОЗ — описание колонок таблицы.
 *
 * Набор отличается от каталога ВБ: у Ozon свои идентификаторы (offer_id, SKU,
 * product_id), остатки по складам и ошибки валидации карточки, зато нет
 * размеров и КИЗ.
 */

import React from 'react'
import { Badge, type BadgeVariant } from '../../../components/ui/Badge'
import { ImagePreviewPlaceholder, ImagePreviewThumb } from '../../../components/ui/ImagePreviewThumb'
import { marketplaceThumbnailUrl } from '../../../shared/media/marketplaceThumbnails'
import type { CatalogColumn } from '../../marketplace-catalog'
import type { OzCatalogProduct } from './types'

/**
 * Статус карточки Ozon: statuses.status приходит машинным (price_sent,
 * imported, moderating, failed_moderation…), человекочитаемое — в state.
 */
export const ozStatusVariant = (status?: string, isArchived?: boolean): BadgeVariant => {
  if (isArchived) return 'gray'
  const value = String(status || '').toLowerCase()
  if (value.includes('fail') || value.includes('error') || value.includes('declin')) return 'red'
  if (
    value.includes('moderat') ||
    value.includes('pending') ||
    value.includes('process') ||
    value.includes('import')
  ) {
    return 'yellow'
  }
  if (
    value.includes('price_sent') ||
    value.includes('ready') ||
    value.includes('sale') ||
    value.includes('publish')
  ) {
    return 'green'
  }
  return 'gray'
}

export const ozStatusLabel = (product: OzCatalogProduct): string =>
  product.is_archived ? 'В архиве' : product.state || product.status || 'Без статуса'

export const rowKeyOf = (product: OzCatalogProduct): string => String(product.id ?? '')

export const photoUrlOf = (product: OzCatalogProduct): string =>
  product.primary_image || (Array.isArray(product.images) ? product.images[0] : '') || ''

export const categoryOf = (product: OzCatalogProduct): string =>
  product.category_name ||
  product.type_name ||
  (product.description_category_id ? `Категория ${product.description_category_id}` : '')

export const formatMoney = (value?: number | null): string =>
  value || value === 0 ? `${Math.round(Number(value)).toLocaleString('ru-RU')} ₽` : '—'

export const formatDateTime = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

export const creationDateOf = (product: OzCatalogProduct) => {
  const value = product.effective_created_at || null
  const pimTime = product.pim_created_at ? new Date(product.pim_created_at).getTime() : Number.NaN
  const effectiveTime = value ? new Date(value).getTime() : Number.NaN
  return {
    value,
    fromPim: Number.isFinite(pimTime) && pimTime === effectiveTime,
  }
}

const dash = <span className="text-gray-300 dark:text-zinc-600">—</span>

const Thumb: React.FC<{ product: OzCatalogProduct }> = ({ product }) => {
  const url = photoUrlOf(product)
  // Размер превью по умолчанию — вертикальный, как в «Каталоге макетов v2»
  if (!url) return <ImagePreviewPlaceholder />
  // Зеркало хранит полноразмерный кадр (211 КБ), а CDN ОЗОН отдаёт готовый
  // уменьшенный — на плитку в 32 пикселя разница в тринадцать раз.
  return <ImagePreviewThumb src={marketplaceThumbnailUrl(url, 250)} alt={product.name || ''} />
}

export const OZ_COLUMNS: Array<CatalogColumn<OzCatalogProduct>> = [
  {
    key: 'product',
    label: 'Товар',
    width: 360,
    minWidth: 260,
    sortColumn: 'name',
    locked: true,
    render: (product, { mode }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        {mode === 'standard' && <Thumb product={product} />}
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-[0.8125rem] text-gray-900 dark:text-zinc-100"
            title={product.name}
          >
            {product.name || 'Без названия'}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <span className="shrink-0 font-mono text-xs text-gray-500 dark:text-zinc-400">
              {product.offer_id || '—'}
            </span>
            {product.brand && (
              <span className="truncate text-xs text-gray-500 dark:text-zinc-400">
                · {product.brand}
              </span>
            )}
          </span>
        </span>
      </span>
    ),
  },
  {
    key: 'offerId',
    label: 'Артикул',
    width: 150,
    minWidth: 110,
    sortColumn: 'offer_id',
    render: (product) => (
      <span className="block truncate font-mono text-[0.8125rem]">{product.offer_id || '—'}</span>
    ),
  },
  {
    key: 'brand',
    label: 'Бренд',
    width: 150,
    minWidth: 110,
    sortColumn: 'brand',
    render: (product) =>
      product.brand ? (
        <span className="block truncate" title={product.brand}>
          {product.brand}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'category',
    label: 'Категория',
    width: 175,
    minWidth: 125,
    render: (product) => {
      const value = categoryOf(product)
      return value ? (
        <span className="block truncate text-gray-700 dark:text-zinc-300" title={value}>
          {value}
        </span>
      ) : (
        dash
      )
    },
  },
  {
    key: 'productId',
    label: 'Ozon ID',
    width: 120,
    minWidth: 100,
    render: (product) =>
      product.product_id ? (
        <span className="block truncate font-mono text-xs text-blue-600 dark:text-blue-400">
          {product.product_id}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'sku',
    label: 'SKU',
    width: 120,
    minWidth: 100,
    sortColumn: 'sku',
    render: (product) =>
      product.sku ? (
        <span className="block truncate font-mono text-xs">{product.sku}</span>
      ) : (
        dash
      ),
  },
  {
    key: 'price',
    label: 'Цена',
    width: 120,
    minWidth: 95,
    sortColumn: 'price',
    align: 'right',
    render: (product) => {
      if (!product.final_price) return dash
      const showOld = Boolean(
        product.old_price && Number(product.old_price) > Number(product.final_price),
      )
      return (
        <span className="block">
          {showOld && (
            <span className="block text-xs text-gray-400 line-through dark:text-zinc-500">
              {formatMoney(product.old_price)}
            </span>
          )}
          <span className="block font-semibold tabular-nums">
            {formatMoney(product.final_price)}
          </span>
        </span>
      )
    },
  },
  {
    key: 'discount',
    label: 'Скидка',
    width: 100,
    minWidth: 85,
    align: 'right',
    render: (product) =>
      product.discount_percent > 0 ? <Badge variant="red">-{product.discount_percent}%</Badge> : dash,
  },
  {
    key: 'minPrice',
    label: 'Мин. цена',
    width: 115,
    minWidth: 95,
    align: 'right',
    render: (product) => (product.min_price ? formatMoney(product.min_price) : dash),
  },
  {
    key: 'stock',
    label: 'Остаток',
    width: 105,
    minWidth: 90,
    sortColumn: 'total_stock',
    align: 'center',
    render: (product) =>
      product.total_stock > 0 ? (
        <Badge variant="blue">{Number(product.total_stock).toLocaleString('ru-RU')}</Badge>
      ) : (
        <span className="tabular-nums text-gray-400 dark:text-zinc-500">0</span>
      ),
  },
  {
    key: 'errors',
    label: 'Ошибки',
    width: 100,
    minWidth: 85,
    align: 'center',
    render: (product) => {
      const count = Array.isArray(product.errors) ? product.errors.length : 0
      return count > 0 ? <Badge variant="red">{count}</Badge> : dash
    },
  },
  {
    key: 'pim',
    label: 'PIM',
    width: 130,
    minWidth: 100,
    align: 'center',
    render: (product) =>
      product.pim_article ? (
        <span
          className="block truncate font-mono text-xs text-indigo-700 dark:text-indigo-300"
          title={product.pim_title || product.pim_article}
        >
          {product.pim_article}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'status',
    label: 'Статус',
    width: 140,
    minWidth: 110,
    align: 'center',
    render: (product) => (
      <Badge variant={ozStatusVariant(product.status, product.is_archived)} dot>
        {ozStatusLabel(product)}
      </Badge>
    ),
  },
  {
    key: 'visibility',
    label: 'Видимость',
    width: 120,
    minWidth: 100,
    align: 'center',
    render: (product) =>
      product.visibility ? (
        <span className="text-xs text-gray-600 dark:text-zinc-300">
          {product.visibility === 'VISIBLE' ? 'На витрине' : 'Скрыт'}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'createdAt',
    label: 'Дата создания',
    width: 145,
    minWidth: 120,
    sortColumn: 'created_at',
    render: (product) => {
      const creationDate = creationDateOf(product)
      return (
        <span
          className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400"
          title={creationDate.fromPim ? 'Создано конвейером PIM' : undefined}
        >
          {formatDateTime(creationDate.value)}
        </span>
      )
    },
  },
  {
    key: 'updatedAt',
    label: 'Обновлено',
    width: 145,
    minWidth: 120,
    sortColumn: 'updated_at',
    render: (product) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {formatDateTime(product.updated_at)}
      </span>
    ),
  },
  {
    key: 'lastSync',
    label: 'Синхронизировано',
    width: 150,
    minWidth: 125,
    render: (product) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {formatDateTime(product.last_sync_at)}
      </span>
    ),
  },
  {
    key: 'barcode',
    label: 'Штрихкод',
    width: 140,
    minWidth: 115,
    render: (product) => {
      const value = product.barcode || (Array.isArray(product.barcodes) ? product.barcodes[0] : '')
      return value ? <span className="block truncate font-mono text-xs">{value}</span> : dash
    },
  },
]

/** Колонки, видимые до того как пользователь настроит своё */
export const OZ_DEFAULT_VISIBLE = [
  'product',
  'brand',
  'category',
  'productId',
  'price',
  'stock',
  'pim',
  'status',
  'createdAt',
]

/** Колонки выгрузки в XLS — идентификаторы Ozon, а не «общие» названия */
export const OZ_XLS_COLUMNS = [
  { key: 'offer_id', header: 'Артикул', value: (p: OzCatalogProduct) => p.offer_id || '' },
  { key: 'product_id', header: 'Ozon ID', value: (p: OzCatalogProduct) => p.product_id || '' },
  { key: 'sku', header: 'SKU', value: (p: OzCatalogProduct) => p.sku || '' },
  { key: 'name', header: 'Название', value: (p: OzCatalogProduct) => p.name || '' },
  { key: 'brand', header: 'Бренд', value: (p: OzCatalogProduct) => p.brand || '' },
  { key: 'category', header: 'Категория', value: (p: OzCatalogProduct) => categoryOf(p) },
  { key: 'price', header: 'Цена', value: (p: OzCatalogProduct) => p.final_price || '' },
  { key: 'discount', header: 'Скидка, %', value: (p: OzCatalogProduct) => p.discount_percent || '' },
  { key: 'stock', header: 'Остаток', value: (p: OzCatalogProduct) => p.total_stock ?? '' },
  { key: 'pim', header: 'Артикул PIM', value: (p: OzCatalogProduct) => p.pim_article || '' },
  { key: 'status', header: 'Статус', value: (p: OzCatalogProduct) => ozStatusLabel(p) },
  {
    key: 'errors',
    header: 'Ошибок карточки',
    value: (p: OzCatalogProduct) => (Array.isArray(p.errors) ? p.errors.length : 0),
  },
  {
    key: 'created_at',
    header: 'Дата создания',
    value: (p: OzCatalogProduct) => formatDateTime(p.effective_created_at),
  },
  {
    key: 'updated_at',
    header: 'Обновлено',
    value: (p: OzCatalogProduct) => formatDateTime(p.updated_at),
  },
]
